import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserInfo, VisualCache } from '../helper/Context';
import { APIGetVisualizations } from '../helper/APIFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine,
  faImages,
  faLightbulb,
  faCog,
  faEye,
  faSave,
  faArrowUp,
  faArrowDown,
  faClock,
  faUsers,
  faPlay,
  faCheckCircle,
  faMagic,
  faExclamationTriangle,
  faTrophy,
  faFire,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { Button, Spinner, Form, Table } from 'react-bootstrap';
import VisualPickerModal from '../components/VisualPickerModal';
import ContentCreationWizard from '../components/ContentCreationWizard';
import CarouselVisCard from '../components/CarouselVisCard';
import CarouselPresentationCard from '../components/CarouselPresentationCard';
import CarouselExplainerCard from '../components/CarouselExplainerCard';
import { 
  loadChannelConfig, 
  saveChannelConfig, 
  getDefaultChannelConfig,
  updateChannelSettings,
  toggleVisualInChannel,
  removeVisualFromChannel
} from '../helper/ChannelConfig';
import { 
  getUserSettings,
  saveUserSettings,
  getDefaultUserSettings
} from '../helper/UserSettings';
import { 
  generateMockEngagementData, 
  getTrendingContent,
  getContentTypeSuggestions
} from '../helper/MockEngagementService';
import { generateContent } from '../helper/ContentGenerationService';
import './ContentDashboard.css';

const ContentDashboard = () => {
  const { userInfo } = useContext(UserInfo);
  const { visCache } = useContext(VisualCache);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('content');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [visuals, setVisuals] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [explainers, setExplainers] = useState([]);
  const [videos, setVideos] = useState([]);
  
  const [channelConfig, setChannelConfig] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [showVisualPicker, setShowVisualPicker] = useState(false);
  const [showCreationWizard, setShowCreationWizard] = useState(false);

  // Suggestion wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Helper to normalize presentations
  const normalizePresentation = (pres) => {
    const normalizedPres = {
      ...pres,
      slides: (pres.slides || []).map(slide => {
        let visual = null;
        if (visCache && slide.visual_id && visCache[slide.visual_id]) {
          visual = visCache[slide.visual_id];
        }
        return {
          ...visual,
          ...slide,
          visualization_url: (slide.visualization_url || slide.visual_url || (visual && visual.visualization_url) || ""),
          thumbnail: (slide.thumbnail || (visual && visual.thumbnail) || ""),
        };
      })
    };
    normalizedPres.thumbnail = pres.thumbnail || (normalizedPres?.slides?.length > 0 ? normalizedPres?.slides[0]?.thumbnail : "");
    return normalizedPres;
  };

  useEffect(() => {
    if (!userInfo || !userInfo.email) return;

    const loadData = async () => {
      setLoading(true);
      
      try {
        const personalDataPromise = new Promise((resolve, reject) => {
          APIGetVisualizations(
            (body) => resolve(body),
            (err) => reject(err),
            visCache
          );
        });

        const data = await personalDataPromise;
        
        setVisuals((data.visuals || []).filter(v => !v.hide_in_library).map(v => {
          v.owner = "self";
          v.topLevelType = "visual";
          return v;
        }));
        
        setPresentations((data.presentations || []).filter(p => !p.hide_in_library).map(p => {
          const pres = normalizePresentation(p);
          if (!pres.thumbnail && pres.slides && pres.slides.length > 0) {
            pres.thumbnail = pres.slides[0].thumbnail;
          }
          pres.owner = "self";
          pres.topLevelType = "presentation";
          return pres;
        }));
        
        setVideos((data.videos || []).filter(v => !v.hide_in_library).map(v => {
          v.owner = "self";
          v.topLevelType = "visual";
          v.type = "video";
          return v;
        }));
        
        setExplainers((data.explainers || []).filter(e => !e.hide_in_library).map(e => {
          e.owner = "self";
          e.topLevelType = "explainer";
          return e;
        }));

        // Load channel config
        let config = loadChannelConfig(userInfo.email);
        if (!config) {
          config = getDefaultChannelConfig(userInfo);
          saveChannelConfig(userInfo.email, config);
        }
        setChannelConfig(config);
        
        // Load user settings
        let settings = getUserSettings(userInfo.email);
        if (!settings) {
          settings = getDefaultUserSettings(userInfo);
          saveUserSettings(userInfo.email, settings);
        }
        // Add channelEmail for wizard usage
        settings.channelEmail = config.settings?.slug || userInfo.email.split('@')[0];
        setUserSettings(settings);
        
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userInfo, visCache]);

  const allVisuals = useMemo(() => {
    return [...visuals, ...presentations, ...explainers, ...videos];
  }, [visuals, presentations, explainers, videos]);

  const engagementData = useMemo(() => {
    if (!channelConfig || !allVisuals.length) return null;
    
    const selectedVisuals = allVisuals.filter(v => 
      channelConfig.selectedVisuals.includes(v.id)
    );
    
    return generateMockEngagementData(selectedVisuals, timeRange);
  }, [allVisuals, channelConfig, timeRange]);

  const suggestions = useMemo(() => {
    if (!channelConfig || !allVisuals.length) return [];
    return getContentTypeSuggestions(allVisuals, channelConfig);
  }, [allVisuals, channelConfig]);

  const handleAddVisual = (visual) => {
    if (!userInfo || !channelConfig) return;
    
    const updatedConfig = toggleVisualInChannel(userInfo.email, visual.id, channelConfig);
    if (updatedConfig) {
      setChannelConfig(updatedConfig);
    }
    setShowVisualPicker(false);
  };

  const handleRemoveVisual = (visualId) => {
    if (!userInfo || !channelConfig) return;
    
    const updatedConfig = removeVisualFromChannel(userInfo.email, visualId, channelConfig);
    if (updatedConfig) {
      setChannelConfig(updatedConfig);
    }
  };

  const handleSaveChannel = () => {
    if (!userInfo || !channelConfig) return;
    
    setSaving(true);
    setTimeout(() => {
      saveChannelConfig(userInfo.email, channelConfig);
      setSaving(false);
    }, 500);
  };

  const handlePreviewChannel = () => {
    if (!userInfo || !channelConfig) return;
    saveChannelConfig(userInfo.email, channelConfig);
    navigate('/channel-preview');
  };

  const handleUpdateSettings = (field, value) => {
    if (!userInfo || !channelConfig) return;
    
    const updates = { [field]: value };
    const updatedConfig = updateChannelSettings(userInfo.email, updates, channelConfig);
    if (updatedConfig) {
      setChannelConfig(updatedConfig);
    }
  };

  const handleAddSuggestedVisual = (visualId) => {
    if (!userInfo || !channelConfig) return;
    
    const updatedConfig = toggleVisualInChannel(userInfo.email, visualId, channelConfig);
    if (updatedConfig) {
      setChannelConfig(updatedConfig);
    }
  };

  const handleGenerateContent = async (contentType) => {
    if (!userSettings) {
      setGenerationError('User settings not loaded');
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setGenerationProgress(0);
    setSelectedContentType(contentType);
    
    try {
      const result = await generateContent(
        contentType,
        userSettings,
        (progress) => setGenerationProgress(progress)
      );
      
      setGeneratedContent(result);
      setWizardStep(3);
    } catch (error) {
      console.error('Content generation error:', error);
      setGenerationError(error.message || 'Failed to generate content');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleUpdateUserSettings = (key, value) => {
    if (!userInfo || !userInfo.email || !userSettings) return;
    
    const updatedSettings = {
      ...userSettings,
      [key]: value
    };
    
    const saved = saveUserSettings(userInfo.email, updatedSettings);
    if (saved) {
      setUserSettings(saved);
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setSelectedContentType(null);
    setGeneratedContent(null);
    setGenerationError(null);
  };

  if (loading) {
    return (
      <div className="content-dashboard-loading">
        <Spinner animation="border" variant="primary" />
        <p>Loading your content library...</p>
      </div>
    );
  }

  if (!channelConfig) {
    return (
      <div className="content-dashboard-error">
        <h3>Unable to load channel configuration</h3>
        <p>Please refresh the page or contact support if the problem persists.</p>
      </div>
    );
  }

  const selectedVisuals = allVisuals.filter(v => 
    channelConfig.selectedVisuals.includes(v.id)
  );

  return (
    <div className="content-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-title-section">
            <h1 className="dashboard-title">Content Dashboard</h1>
            <p className="dashboard-subtitle">Curate and manage your channel content</p>
          </div>
          <div className="dashboard-header-actions">
            <Button 
              variant="outline-primary" 
              onClick={handleSaveChannel}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} />
              {saving ? ' Saving...' : ' Save Changes'}
            </Button>
            <Button 
              variant="primary" 
              onClick={handlePreviewChannel}
              disabled={selectedVisuals.length === 0}
            >
              <FontAwesomeIcon icon={faEye} />
              Preview Channel
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-tabs">
          <button 
            className={`dashboard-tab ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <FontAwesomeIcon icon={faImages} />
            Content Selection
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <FontAwesomeIcon icon={faChartLine} />
            Analytics
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <FontAwesomeIcon icon={faCog} />
            Settings
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Tab 1: Content Selection */}
        {activeTab === 'content' && (
          <div className="content-tab">
            <div className="tab-intro">
              <p>Select visuals from your library or create new content to appear in your channel feed.</p>
            </div>

            {/* Add Content Button */}
            <div className="add-content-section">
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => setShowCreationWizard(true)}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Content to Channel
              </Button>
            </div>

            {/* Selected Content Grid */}
            {selectedVisuals.length === 0 ? (
              <div className="empty-selection-state">
                <div className="empty-selection-icon">📱</div>
                <h3>No content selected yet</h3>
                <p>Click "Add Content to Channel" to start building your feed</p>
              </div>
            ) : (
              <div className="selected-content-section">
                <div className="section-header">
                  <h3>
                    <FontAwesomeIcon icon={faCheckCircle} className="section-icon" />
                    Channel Content ({selectedVisuals.length})
                  </h3>
                  <p className="section-description">
                    These items will appear in your channel feed. Click the remove button to deselect.
                  </p>
                </div>
                
                <div className="selected-content-grid">
                  {selectedVisuals.map((visual) => (
                    <div key={visual.id} className="selected-content-item">
                      <div className="selected-content-card">
                        {visual.type === 'presentation' || visual.type === 'slideshow' || visual.type === 'scrollytelling' || visual.topLevelType === 'presentation' ? (
                          <CarouselPresentationCard
                            presentation={visual}
                            onPresDelete={() => {}}
                            onPresEdit={() => {}}
                            blockActions={true}
                          />
                        ) : visual.topLevelType === 'explainer' || visual.type === 'explainer' || visual.type === 'tour' ? (
                          <CarouselExplainerCard
                            explainer={visual}
                            onExplainerDelete={() => {}}
                            onExplainerEdit={() => {}}
                            blockActions={true}
                          />
                        ) : (
                          <CarouselVisCard
                            vis={visual}
                            onVisDelete={() => {}}
                            onVisEdit={() => {}}
                            blockActions={true}
                          />
                        )}
                      </div>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        className="remove-content-btn"
                        onClick={() => handleRemoveVisual(visual.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Remove from Channel
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visual Picker Modal */}
            <VisualPickerModal
              show={showVisualPicker}
              onHide={() => setShowVisualPicker(false)}
              onSelect={handleAddVisual}
              userInfo={userInfo}
              defaultView="my-visuals"
              filterVisualIdsList={channelConfig?.selectedVisuals || []}
              allowAllContentTypes={true}
              additionalVisuals={[...presentations, ...explainers, ...videos]}
            />

            {/* Content Creation Wizard */}
            <ContentCreationWizard
              show={showCreationWizard}
              onHide={() => setShowCreationWizard(false)}
              userSettings={userSettings}
              onUpdateUserSettings={handleUpdateUserSettings}
              onGenerate={handleGenerateContent}
              isGenerating={isGenerating}
              generationProgress={generationProgress}
              generatedContent={generatedContent}
              generationError={generationError}
              onReset={resetWizard}
              onOpenManualPicker={() => setShowVisualPicker(true)}
              visuals={visuals}
              presentations={presentations}
              explainers={explainers}
              videos={videos}
              fromChannel={true}
            />
          </div>
        )}

        {/* Tab 2: Analytics */}
        {activeTab === 'analytics' && (
          <div className="analytics-tab">
            {selectedVisuals.length === 0 ? (
              <div className="empty-analytics-state">
                <div className="empty-analytics-icon">📊</div>
                <h3>No content selected</h3>
                <p>Select some content in the Content Selection tab to see analytics</p>
                <Button variant="primary" onClick={() => setActiveTab('content')}>
                  Go to Content Selection
                </Button>
              </div>
            ) : (
              <>
                <div className="analytics-controls">
                  <div className="time-range-selector">
                    <Form.Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                      <option value="7d">Last 7 days</option>
                      <option value="30d">Last 30 days</option>
                      <option value="90d">Last 90 days</option>
                      <option value="1y">Last year</option>
                    </Form.Select>
                  </div>
                  <div className="analytics-note">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>Showing simulated data for testing</span>
                  </div>
                </div>

                {engagementData && (
                  <>
                    {/* KPI Cards */}
                    <div className="kpi-grid">
                      <div className="kpi-card">
                        <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                          <FontAwesomeIcon icon={faUsers} />
                        </div>
                        <div className="kpi-content">
                          <div className="kpi-label">Total Visitors</div>
                          <div className="kpi-value">{engagementData.overview.totalVisitors.toLocaleString()}</div>
                          <div className="kpi-change positive">
                            <FontAwesomeIcon icon={faArrowUp} /> +12% vs last period
                          </div>
                        </div>
                      </div>
                      
                      <div className="kpi-card">
                        <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                          <FontAwesomeIcon icon={faArrowUp} />
                        </div>
                        <div className="kpi-content">
                          <div className="kpi-label">Repeat Visitors</div>
                          <div className="kpi-value">{engagementData.overview.repeatVisitors.toLocaleString()}</div>
                          <div className="kpi-subtext">
                            {Math.round((engagementData.overview.repeatVisitors / engagementData.overview.totalVisitors) * 100)}% return rate
                          </div>
                        </div>
                      </div>
                      
                      <div className="kpi-card">
                        <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                          <FontAwesomeIcon icon={faClock} />
                        </div>
                        <div className="kpi-content">
                          <div className="kpi-label">Avg. Time Engaged</div>
                          <div className="kpi-value">{engagementData.overview.avgTimeEngaged}</div>
                          <div className="kpi-change positive">
                            <FontAwesomeIcon icon={faArrowUp} /> +8% vs last period
                          </div>
                        </div>
                      </div>
                      
                      <div className="kpi-card">
                        <div className="kpi-icon-wrapper" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                          <FontAwesomeIcon icon={faPlay} />
                        </div>
                        <div className="kpi-content">
                          <div className="kpi-label">Content Views / Session</div>
                          <div className="kpi-value">{engagementData.overview.contentViewsPerSession}</div>
                          <div className="kpi-change positive">
                            <FontAwesomeIcon icon={faArrowUp} /> +15% vs last period
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Trending Content Table */}
                    <div className="trending-section">
                      <h3>
                        <FontAwesomeIcon icon={faFire} className="section-icon" />
                        Trending Content
                      </h3>
                      <div className="trending-table-wrapper">
                        <Table hover className="trending-table">
                          <thead>
                            <tr>
                              <th>Rank</th>
                              <th>Content</th>
                              <th>Type</th>
                              <th>Views</th>
                              <th>Likes</th>
                              <th>Avg. Time</th>
                              <th>Trend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {engagementData.trending.slice(0, 10).map((item, idx) => {
                              const visual = selectedVisuals.find(v => v.id === item.visualId);
                              if (!visual) return null;
                              
                              return (
                                <tr key={visual.id}>
                                  <td className="rank-cell">
                                    {idx === 0 && <FontAwesomeIcon icon={faTrophy} className="trophy-icon gold" />}
                                    {idx === 1 && <FontAwesomeIcon icon={faTrophy} className="trophy-icon silver" />}
                                    {idx === 2 && <FontAwesomeIcon icon={faTrophy} className="trophy-icon bronze" />}
                                    {idx > 2 && <span className="rank-number">#{idx + 1}</span>}
                                  </td>
                                  <td className="content-cell">
                                    <div className="content-preview">
                                      {visual.thumbnail && (
                                        <img src={visual.thumbnail} alt="" className="content-thumbnail" />
                                      )}
                                      <span className="content-title">{visual.title || 'Untitled'}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="type-badge">{visual.type || 'visual'}</span>
                                  </td>
                                  <td>{item.views.toLocaleString()}</td>
                                  <td>{item.likes.toLocaleString()}</td>
                                  <td>{item.avgTime}</td>
                                  <td>
                                    <span className={`trend-value ${item.trend >= 0 ? 'positive' : 'negative'}`}>
                                      <FontAwesomeIcon icon={item.trend >= 0 ? faArrowUp : faArrowDown} />
                                      {Math.abs(item.trend)}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </div>

                    {/* Audience Segments */}
                    <div className="audience-section">
                      <h3>
                        <FontAwesomeIcon icon={faUsers} className="section-icon" />
                        Audience Segments
                      </h3>
                      <div className="audience-grid">
                        {engagementData.audienceSegments.map((segment, idx) => (
                          <div key={idx} className="audience-card">
                            <div className="audience-header">
                              <h4>{segment.role}</h4>
                              <div className="audience-count">{segment.count.toLocaleString()} visitors</div>
                            </div>
                            <div className="audience-stats">
                              <div className="audience-stat">
                                <div className="stat-label">Avg. Time</div>
                                <div className="stat-value">{segment.avgTime}</div>
                              </div>
                              <div className="audience-stat">
                                <div className="stat-label">Reading Style</div>
                                <div className="stat-value">{segment.readingStyle}</div>
                              </div>
                            </div>
                            <div className="audience-interests">
                              <div className="interests-label">Top Interests:</div>
                              <div className="interests-tags">
                                {segment.topInterests.map((interest, iIdx) => (
                                  <span key={iIdx} className="interest-tag">{interest}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab 3: Settings */}
        {activeTab === 'settings' && (
          <div className="settings-tab">
            <div className="settings-form">
              <div className="settings-section">
                <h3>Channel Information</h3>
                
                <Form.Group className="mb-4">
                  <Form.Label>Channel Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={channelConfig.channelName}
                    onChange={(e) => handleUpdateSettings('channelName', e.target.value)}
                    placeholder="My Channel"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Channel Avatar (emoji)</Form.Label>
                  <Form.Control
                    type="text"
                    value={channelConfig.avatar}
                    onChange={(e) => handleUpdateSettings('avatar', e.target.value)}
                    placeholder="📱"
                    maxLength={2}
                  />
                  <Form.Text muted>Choose an emoji to represent your channel</Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={channelConfig.description}
                    onChange={(e) => handleUpdateSettings('description', e.target.value)}
                    placeholder="Tell visitors what your channel is about..."
                  />
                </Form.Group>
              </div>

              <div className="settings-section">
                <h3>Mission Statement</h3>
                <p className="section-helper-text">
                  Add your organization's mission statement to help AI generate relevant content suggestions 
                  based on your goals and values. This will be used alongside your channel content and analytics 
                  to recommend visuals that align with your mission.
                </p>
                
                <Form.Group className="mb-4">
                  <Form.Label>Organization Mission Statement</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={channelConfig.missionStatement || ''}
                    onChange={(e) => handleUpdateSettings('missionStatement', e.target.value)}
                    placeholder="Paste your organization's mission statement here. For example: 'Our mission is to advance scientific discovery through innovative research and education, making complex biomedical concepts accessible to researchers, educators, and the public...'"
                  />
                  <Form.Text muted>
                    This will be used to generate personalized content suggestions that align with your organization's goals.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Institution Profile</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={channelConfig.institutionProfile || ''}
                    onChange={(e) => handleUpdateSettings('institutionProfile', e.target.value)}
                    placeholder="e.g., 'Large teaching hospital specializing in neuroscience', 'Small liberal arts college', 'Research foundation focused on rare diseases'"
                  />
                  <Form.Text muted>
                    Describe your institution type to help tailor content suggestions appropriately.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="settings-section">
                <h3>Privacy & Access</h3>
                
                <Form.Group className="mb-4">
                  <Form.Label>Privacy Setting</Form.Label>
                  <Form.Select
                    value={channelConfig.settings.privacy}
                    onChange={(e) => {
                      const updatedSettings = { ...channelConfig.settings, privacy: e.target.value };
                      handleUpdateSettings('settings', updatedSettings);
                    }}
                  >
                    <option value="public">Public - Anyone can view</option>
                    <option value="unlisted">Unlisted - Only people with link can view</option>
                    <option value="private">Private - Only you can view</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Channel URL Slug</Form.Label>
                  <div className="url-preview">
                    <span className="url-base">snorkle.ai/channel/</span>
                    <Form.Control
                      type="text"
                      value={channelConfig.settings.slug}
                      onChange={(e) => {
                        const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '');
                        const updatedSettings = { ...channelConfig.settings, slug };
                        handleUpdateSettings('settings', updatedSettings);
                      }}
                      placeholder="username"
                      className="slug-input"
                    />
                  </div>
                  <Form.Text muted>Letters, numbers, hyphens, and underscores only</Form.Text>
                </Form.Group>
              </div>

              <div className="settings-section">
                <h3>Channel Statistics</h3>
                <div className="channel-stats">
                  <div className="channel-stat">
                    <div className="stat-label">Total Content</div>
                    <div className="stat-value">{selectedVisuals.length}</div>
                  </div>
                  <div className="channel-stat">
                    <div className="stat-label">Created</div>
                    <div className="stat-value">
                      {new Date(channelConfig.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="channel-stat">
                    <div className="stat-label">Last Updated</div>
                    <div className="stat-value">
                      {new Date(channelConfig.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentDashboard;
